import { useCallback, useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Image, Paragraph, Text } from '@rover/kibble/core';
import FilterChipGroup from '@rover/kibble/patterns/chips/FilterChipGroup/FilterChipGroup';
import { PetInfoQuestion } from '@rover/types/src/Callout';

type Props = {
  callout: PetInfoQuestion;
  onChange: (callout: PetInfoQuestion, newValue: string) => void;
  onClose: (callout: PetInfoQuestion) => void;
};

function PetInfoCallout({ callout, onChange, onClose }: Props): JSX.Element {
  const { t } = useLingui();
  const handleChange = useCallback(
    (value: string | number | Array<string | number> | null): void => {
      if (value === null) return;
      onChange(callout, value as string);
    },
    [onChange, callout]
  );

  const handleDismiss = useCallback((): void => {
    onClose(callout);
  }, [onClose, callout]);

  const options = useMemo(
    () =>
      callout.answers.map((answer) => ({
        id: answer.id,
        label: answer.label,
        value: answer.id,
      })),
    [callout.answers]
  );

  return (
    <Box
      p="4x"
      pb={['32x', '4x', '4x']}
      background={callout.backgroundColor}
      data-testid="pet-info-callout"
      position="relative"
    >
      <Flex flexDirection="column" gap="3x" pr={['10x', '10x', '48x']}>
        <Paragraph size="200" mb="0x" pr="10x">
          <Text fontWeight="semibold">{callout.question}</Text>
        </Paragraph>
        <Flex flexDirection="row" gap="2x" flexWrap="wrap">
          {options.length === 1 ? (
            <Button size="small" onPress={() => handleChange(options[0].value)} mt="2x" mb="4x">
              {options[0].label}
            </Button>
          ) : (
            <FilterChipGroup
              groupLabel=""
              options={options}
              canSelectMultiple={false}
              isDeselectable={false}
              // Answering runs a search and closes the callout, so not on arrow keys.
              selectsOnArrowKeys={false}
              size="small"
              selectedIcon={null}
              onChange={handleChange}
              shouldWrap={false}
            />
          )}
        </Flex>
      </Flex>
      <Box position="absolute" top="3x" right="2x" zIndex="9999999">
        <Button
          variant="flat"
          p="2x"
          onPress={handleDismiss}
          aria-label={t`Close pet information callout`}
          data-testid="close-callout-button"
        >
          <Close height="16" width="16" />
        </Button>
      </Box>
      <Box
        position="absolute"
        bottom={['-18px', '-16px', '-16px']}
        left={['65px', 'auto', 'auto']}
        right={['65px', '4x', '4x']}
        width={['auto', '200px', '250px']}
        zIndex="999999"
        sx={{
          '@media (min-width: 470px) and (max-width: 767px)': {
            bottom: '-20px',
            left: 'auto',
            right: '25px',
            maxWidth: '300px',
          },
        }}
      >
        <Image
          src={callout.image}
          description={callout.imageDescription}
          height="auto"
          width="100%"
        />
      </Box>
    </Box>
  );
}

export default PetInfoCallout;
